import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {
  adjustToTimeZone,
  adjustToTimeZoneSafely,
  firstOrUndefined,
  getObjectId,
  PipelineFlowTransportSearchCriteria,
  pipelineFlowTransportsSelector,
  startOfDay,
  toFlowAllocationTypeSafely,
  toTimeGroupSafely,
  useSearchPipelineActivityQuery,
} from "@ots-frontend/ots-data";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {
  PipelineFlowTransportDataTable
} from "../../components/data-table/PipelineFlowTransportDataTable/PipelineFlowTransportDataTable";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {selectUIPipelineFlowTransportSearchFilter} from "../../store/entity/pipeline-flow-transport/selectors";
import {useOtsUISelector} from "../../hooks/useOtsUISelector";
import {useDispatch} from "react-redux";
import {setUIPipelineFlowTransportSearchFilter} from "../../store/entity/pipeline-flow-transport/actions";
import {
  PipelineFlowTransportSearchToolbar
} from "../../components/toolbar/PipelineFlowTransportSearchToolbar/PipelineFlowTransportSearchToolbar";
import {isEqual} from "lodash";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {
  PipelineFlowTransportSearchFormValues
} from "../../components/form/PipelineFlowTransportSearchForm/PipelineFlowTransportSearchForm";
import {
  PipelineFlowTransportExportLink
} from "../../components/exportable/PipelineFlowTransportExportLink/PipelineFlowTransportExportLink";

export type PipelineFlowTransportSearchPageProps = {}

export const PipelineFlowTransportSearchPage = (props: PipelineFlowTransportSearchPageProps) => {
  const {regionId, appCategory} = useApp();
  const {t} = useTranslation(["common"]);

  const dispatch = useDispatch()

  const criteria = useOtsUISelector(selectUIPipelineFlowTransportSearchFilter)

  const selectPipelineFlowTransports = useMemo(() => {
    return pipelineFlowTransportsSelector();
  }, []);

  const searchArg: PipelineFlowTransportSearchCriteria | typeof skipToken = useMemo(() => {
    if (regionId && criteria?.fromDttm) {
      return {
        ...criteria,
        fromDttm: adjustToTimeZone(criteria.fromDttm).getTime(),
        toDttm: adjustToTimeZoneSafely(criteria.toDttm)?.getTime(),
        regionIds: [regionId],
        categories: appCategory ? [appCategory.code] : undefined,
      }
    } else {
      return skipToken
    }
  }, [criteria, regionId, appCategory])

  const {pipelineFlowTransports, isFetching, refetch} = useSearchPipelineActivityQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      pipelineFlowTransports: selectPipelineFlowTransports(result, {regionId: regionId}),
      isFetching: result.isFetching,
    })
  });

  const onFilterChange = useCallback((values: PipelineFlowTransportSearchFormValues) => {
    const freightTypeId = getObjectId(values.freightType)
    const serviceTypeId = getObjectId(values.serviceType)
    const pipelineId = getObjectId(values.pipeline)
    const pipelineCompanyId = getObjectId(values.pipelineCompany)

    const newCriteria: PipelineFlowTransportSearchCriteria = {
      fromDttm: values.fromDate.getTime(),
      toDttm: values.toDate?.getTime(),
      freightTypeIds: freightTypeId ? [freightTypeId] : undefined,
      serviceTypeIds: serviceTypeId ? [serviceTypeId] : undefined,
      pipelineIds: pipelineId ? [pipelineId] : undefined,
      pipelineCompanyIds: pipelineCompanyId ? [pipelineCompanyId] : undefined,
      timeGroup: values.timeGroup?.code,
      allocationType: values.allocationType?.code,
    }

    let criteriaChange = false
    if (criteria?.fromDttm !== newCriteria.fromDttm) {
      criteriaChange = true
    }
    if (criteria?.toDttm !== newCriteria.toDttm) {
      criteriaChange = true
    }
    if (!isEqual(criteria?.freightTypeIds, newCriteria.freightTypeIds)) {
      criteriaChange = true
    }
    if (!isEqual(criteria?.serviceTypeIds, newCriteria.serviceTypeIds)) {
      criteriaChange = true
    }
    if (!isEqual(criteria?.pipelineIds, newCriteria.pipelineIds)) {
      criteriaChange = true
    }
    if (!isEqual(criteria?.pipelineCompanyIds, newCriteria.pipelineCompanyIds)) {
      criteriaChange = true
    }
    if (criteria?.timeGroup !== newCriteria.timeGroup) {
      criteriaChange = true
    }
    if (criteria?.allocationType !== newCriteria.allocationType) {
      criteriaChange = true
    }

    if (!criteriaChange) {
      if (refetch) {
        refetch()
      }
    } else {
      dispatch(setUIPipelineFlowTransportSearchFilter({
        ...newCriteria,
      }))
    }
  }, [criteria, refetch, dispatch]);

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs p="sm"/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.entity.pipelineflow.search.title", "Search Flow Tickets")}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t('common.action.plural', "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <PipelineFlowTransportExportLink data={pipelineFlowTransports}>
                          <Menu.Item>
                            {t("common.action.export", "Export", {ns: "common"})}
                          </Menu.Item>
                        </PipelineFlowTransportExportLink>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <PipelineFlowTransportSearchToolbar
                    defaultValues={{
                      fromDate: criteria?.fromDttm ? new Date(criteria?.fromDttm) : startOfDay(new Date()),
                      toDate: criteria?.toDttm ? new Date(criteria?.toDttm) : undefined,
                      freightType: firstOrUndefined(criteria?.freightTypeIds),
                      serviceType: firstOrUndefined(criteria?.serviceTypeIds),
                      pipeline: firstOrUndefined(criteria?.pipelineIds),
                      pipelineCompany: firstOrUndefined(criteria?.pipelineCompanyIds),
                      timeGroup: toTimeGroupSafely(criteria?.timeGroup),
                      allocationType: toFlowAllocationTypeSafely(criteria?.allocationType),
                    }}
                    options={{
                      regionId: regionId,
                      category: appCategory,
                    }}
                    submitting={isFetching}
                    onSubmit={onFilterChange}/>
                </Card>
                <PipelineFlowTransportDataTable
                  withBorder
                  records={pipelineFlowTransports}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isFetching}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
